import { Tooltip } from "antd";
import dayjs from "dayjs";

const getNextSevenDays = (targetDay: any) => {
  const days = [];

  const currentDate = new Date();

  for (let i = 0; i < 7; i++) {
    const nextDate = new Date(targetDay);
    nextDate.setDate(targetDay.getDate() + i);

    const day = nextDate.getDate();
    const month = nextDate.getMonth() + 1;
    const year = nextDate.getFullYear();
    const weekDay = capitalized(
      nextDate
        .toLocaleDateString("pt-BR", { weekday: "long" })
        .split("-")[0]
        .trim()
    );
    const label = formatDates(
      { day, month, year, weekDay },
      nextDate,
      currentDate
    );
    days.push({ day, month, year, weekDay, label, key: i, date: nextDate });
  }

  return days;
};

const formatArrayFromDates = (dates: any) => {
  return dates.map(
    (date: any) =>
      `${date.day < 10 ? `0${date.day}` : date.day}-${date.month < 10 ? `0${date.month}` : date.month
      }-${date.year}`
  );
};

const prepareDateToShow = (date: any, currentDate = new Date()) => {
  const [year, month, day] = date.split("-").map(Number);
  const utcDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));

  const weekOfDay = utcDate.getDay();
  const weekDay = capitalizedWeekDay(weekOfDay);

  const label = formatDates(
    { day, month, year, weekDay },
    utcDate,
    currentDate
  );

  return { day, month, year, weekDay, label };
};

const capitalizedWeekDay = (weekOfDay: number) => {
  switch (weekOfDay) {
    case 0:
      return "Domingo";
    case 1:
      return "Segunda-feira";
    case 2:
      return "Terça-feira";
    case 3:
      return "Quarta-feira";
    case 4:
      return "Quinta-feira";
    case 5:
      return "Sexta-feira";
    case 6:
      return "Sábado";
    default:
      return "";
  }
};

const capitalized = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const formatDates = (date: any, targetDate: any, currentDate: any) => {
  const { day, month, year, weekDay } = date;

  currentDate.setHours(0, 0, 0, 0);
  targetDate.setHours(0, 0, 0, 0);

  let nameDay = "";

  if (targetDate.getTime() === currentDate.getTime()) {
    nameDay = "Hoje - ";
  }

  if (targetDate.getTime() === currentDate.getTime() + 24 * 60 * 60 * 1000) {
    nameDay = "Amanhã - ";
  }

  return `${nameDay}${weekDay}, ${day} de ${getMonthName(month)}, ${year}`;
};

const getMonthName = (month: number) => {
  const monthNames = [
    "Janeiro",
    "Fevereiro",
    "Março",
    "Abril",
    "Maio",
    "Junho",
    "Julho",
    "Agosto",
    "Setembro",
    "Outubro",
    "Novembro",
    "Dezembro",
  ];
  return monthNames[month - 1];
};



/**
 * Cuts long values to the speficied maximum length with a `<Tooltip>`
 * @param value The value to cut
 * @param maxLength The maximum length of the text
 * @param ellipsis Will replace the text that was cut
 * @returns {string|JSX.Element} `string` if shorter than `maxLength` and the ellipsis size
 * 
 * @example A small piece of text
 * ```js
 * let text = 'Hello, World!';
 * 
 * let x = cutLongText(text);
 * //  ^ = 'Hello, World!'
 * ```
 * 
 * @example A medium piece of text
 * ```js
 * let text = 'Can we get some free food here?';
 * 
 * let x = cutLongText(text);
 * //  ^ = 'Can we get some free food here?'
 * // maxLength is 28 by default + 3 from the `ellipsis` = 31 maximum length
 * // Just so happens that `text` is 31 characters long, thus returns the original text
 * // Replacing 'we' with 'you' would return a `<Tooltip>`
 * ```
 * 
 * @example A large piece of text
 * ```js
 * let text = 'Never gonna give you up, never gonna let you down';
 * 
 * let x = cutLongText(text);
 * //  ^ = <Tooltip title={text}>Never gonna give you up, nev...</Tooltip>
 * ```
 */
const cutLongText = (value: any, maxLength: number = 28, ellipsis: string = '...'): string|JSX.Element => {
  if (!value) {
    return '';
  }
  
  ellipsis = ellipsis.trim();
  if(!ellipsis.length) {
    ellipsis = '...';
  }

  let text = value.toString();

  if (value.length > (maxLength + ellipsis.length)) {
    text = text.substr(0, maxLength) + ellipsis;

    return (<>
      <Tooltip placement="top" title={value}>{text}</Tooltip>
    </>);
  }

  return text;
};

/** 
 * Function to download data as an Excel `.xlsx` file in the browser
 * @param data The data to be exported, expected to be an array of objects
 * @param filename The name of the file to be downloaded
 * @param sheetname The name of the sheet in the Excel file
 */

const downloadExcel = async (data: any, filename: string = 'DataSheetExported', sheetname: string = 'Sheet1') => {
  const XLSX = await import('xlsx');
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetname);
  XLSX.writeFile(workbook, filename + '.xlsx');
};

const authorizedAccess = (
  modulePermissions: string[],
  permissions: string[]
) => permissions.some((permission: string) => modulePermissions.includes(permission));

const formatCurrency = (price: number) => (price || 0).toLocaleString('pt-PT', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2
});

const formatCurrencyToExport = (price: number) => parseFloat((price || 0).toFixed(2));

const formatDatePt = (date: string | number | dayjs.Dayjs | Date) => {
  const newDate = dayjs(date).format("DD/MM/YYYY") ?? "";

  return newDate;
};

const limitServicesToExportPdf = 300;



/**
 * Compares 2 strings - to be used for sorting text
 */
const compareSortString = (a: string, b: string): number => a.localeCompare(b, undefined, {
  usage: 'sort',
  sensitivity: 'base',
  ignorePunctuation: true,
});

/** 
 * Shadows `import.meta.env` with the following changes:
 * - `DEV` will be `true` if the `MODE` is set to `'development'`
 * - `PROD` will be `true` if the `MODE` is set to `'production'`
 */
const env = {
  ...import.meta.env,
  DEV: import.meta.env.DEV || import.meta.env.MODE === 'development',
  PROD: import.meta.env.PROD || import.meta.env.MODE === 'production'
};


export {
  env,
  getNextSevenDays,
  getMonthName,
  formatDates,
  prepareDateToShow,
  formatArrayFromDates,
  cutLongText,
  downloadExcel,
  authorizedAccess,
  capitalized,
  formatCurrency,
  formatCurrencyToExport,
  formatDatePt,
  limitServicesToExportPdf,
  compareSortString,
};
